import { Fragment, type ReactNode } from 'react';

import { Link } from '~/modules/router/components';

const URL_PATTERN = /https?:\/\/\S+/gi;
const TRAILING_PUNCTUATION_PATTERN = /[.,;:!?'"]+$/;
const DELIMITER_PAIRS = [
  ['(', ')'],
  ['[', ']'],
  ['{', '}'],
] as const;
const LINKIFY_CUSTOM_FIELD_URLS_WHITELIST = ['vestigas.com'] as const;

function stripTrailingPunctuation(candidate: string): string {
  let url = candidate.replace(TRAILING_PUNCTUATION_PATTERN, '');

  while (true) {
    const unmatchedClosingDelimiter = DELIMITER_PAIRS.find(
      ([opening, closing]) =>
        url.endsWith(closing) &&
        url.split(closing).length > url.split(opening).length,
    );

    if (!unmatchedClosingDelimiter) {
      return url;
    }

    url = url.slice(0, -1).replace(TRAILING_PUNCTUATION_PATTERN, '');
  }
}

function isWhitelistedUrl(candidate: string): boolean {
  try {
    const { hostname } = new URL(candidate);

    return LINKIFY_CUSTOM_FIELD_URLS_WHITELIST.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
    );
  } catch {
    return false;
  }
}

type TextSegment = { readonly type: 'text'; readonly value: string };
type UrlSegment = { readonly type: 'url'; readonly value: string };

/**
 * Splits text into alternating text and URL segments, matching absolute
 * http(s) URLs embedded anywhere in the string. Trailing sentence
 * punctuation (e.g. a period ending the sentence) is excluded from the URL
 * segment and kept in the surrounding text.
 */
function splitTextAndUrls(text: string): Array<TextSegment | UrlSegment> {
  const segments: Array<TextSegment | UrlSegment> = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index;
    const url = stripTrailingPunctuation(match[0]);

    if (!url || !isWhitelistedUrl(url)) {
      continue;
    }

    if (start > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, start) });
    }

    segments.push({ type: 'url', value: url });
    lastIndex = start + url.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return segments;
}

export type CustomFieldValueProps = {
  readonly value: unknown;
};

/**
 * Renders a formatted custom-field display value, turning whitelisted
 * absolute http(s) URLs into links that open in a new tab. Values that are
 * not strings, or strings without a whitelisted URL, render exactly as before.
 */
export function CustomFieldValue({ value }: CustomFieldValueProps): ReactNode {
  if (typeof value !== 'string' || value === '') {
    return value as ReactNode;
  }

  const segments = splitTextAndUrls(value);

  if (segments.every((segment) => segment.type === 'text')) {
    return value;
  }

  return (
    <>
      {segments.map((segment, index) =>
        segment.type === 'url' ? (
          <Link
            key={index}
            rel="noopener noreferrer"
            target="_blank"
            to={segment.value}
          >
            {segment.value}
          </Link>
        ) : (
          <Fragment key={index}>{segment.value}</Fragment>
        ),
      )}
    </>
  );
}
